// src/components/chat/conversation/conversation.hooks.ts
import * as React from "react";
import {
    buildConversationWsUrl,
    createConversation,
    createMessageJson,
    createMessageMultipart,
    createTheme,
    getCanonicalConversation,
    getUserId,
    listConversationsByTheme,
    listMessages,
    listMessagesAround,
    listMyThemes,
} from "../../../api/chat/chat";
import {
    createReconnectingWs,
    type ReconnectingWs,
} from "../../../utils/reconnecting-ws";
import { ENVIRONMENT } from "../../../env";
import type {
    ConversationResponse,
    MessageResponse,
    ThemeResponse,
    UUID,
} from "../../../api/chat/chat.types";

import { normalizeAvatarUrl } from "../../../utils/avatar-url";
import { ensureValidToken } from "../../../utils/ensure-valid-token";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useAppBoot } from "../../../contexts/app-boot-context";
import { trackEvent } from "../../../utils/analytics";

const log = (...args: any[]) => {
    if (ENVIRONMENT === "DEV") console.log("[use-conversation]", ...args);
};

const PAGE_SIZE = 10;

type WsIncoming =
    | {
        type: "message.created";
        conversation_id: string;
        data: MessageResponse;
    }
    | {
        type: "message.seen";
        conversation_id: string;
        data: { message_ids: string[]; seen_at: string; seen_by: string };
    }
    | {
        type: string;
        [k: string]: any;
    };

const safeJsonParse = (txt: string): any => {
    try {
        return JSON.parse(txt);
    } catch {
        return null;
    }
};

const pickDefaultTheme = (themes: ThemeResponse[]): ThemeResponse | null => {
    if (!themes || themes.length === 0) return null;

    const nonOnboarding = themes.filter((t) => t.is_onboarding === false);
    if (nonOnboarding.length > 0) return nonOnboarding[0];

    return themes[0];
};

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

/** "20 Jan 26 • 6:01 am" or "6:01 am" if today */
export function formatMessageDateTime(v: unknown): string {
    const d = v instanceof Date ? v : new Date(String(v ?? ""));
    const date = isNaN(d.getTime()) ? new Date() : d;

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    let hours = date.getHours();
    const minutes = pad2(date.getMinutes());
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    if (hours === 0) hours = 12;

    const timeStr = `${hours}:${minutes} ${ampm}`;

    if (isToday) {
        return timeStr;
    }

    const day = pad2(date.getDate());
    const month = date.toLocaleString(undefined, { month: "short" });
    const year = String(date.getFullYear()).slice(-2);

    return `${day} ${month} ${year} • ${timeStr}`;
}

export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const k = 1024;

    const i = Math.min(
        Math.floor(Math.log(bytes) / Math.log(k)),
        units.length - 1,
    );
    const val = bytes / Math.pow(k, i);

    const fixed = i === 0 ? 0 : val < 10 ? 1 : 0;
    return `${val.toFixed(fixed)}${units[i]}`;
}

function toNumber(v: any): number | null {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

export function getFileMeta(f: any): { typeLabel: string; sizeLabel: string } {
    const name = String(f?.name ?? "");
    const ext = (name.split(".").pop() ?? "").trim().toUpperCase();

    const mime = String(f?.file_type ?? f?.mime_type ?? f?.mime ?? "").toLowerCase();
    const typeLabel = ext ||
        (mime.includes("pdf")
            ? "PDF"
            : mime.includes("image")
            ? "IMAGE"
            : mime.includes("video")
            ? "VIDEO"
            : "FILE");

    const bytes = toNumber(f?.file_size) ??
        toNumber(f?.size_bytes) ??
        toNumber(f?.size) ??
        0;

    return { typeLabel, sizeLabel: formatBytes(bytes) };
}

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]);
const VIDEO_EXTS = new Set(["mp4", "webm", "mov"]);

export type PreviewKind = "image" | "video" | "pdf" | null;

export function getPreviewKind(name: string, mime?: string): PreviewKind {
    const ext = (name.split(".").pop() ?? "").trim().toLowerCase();
    const m = (mime ?? "").toLowerCase();

    if (IMAGE_EXTS.has(ext) || m.startsWith("image/")) return "image";
    if (VIDEO_EXTS.has(ext) || m.startsWith("video/")) return "video";
    if (ext === "pdf" || m === "application/pdf") return "pdf";
    return null;
}

export type UiMessageStatus = "pending" | "sent" | "failed";

export type LocalFilePreview = {
    name: string;
    type: string;
    size: number;
    previewUrl: string | null; // blob URL for images, null for non-images
};

export type UiMessage = Omit<MessageResponse, "id"> & {
    id: UUID;
    server_id: UUID | null;
    ui_id: string;
    ui_status: UiMessageStatus;
    ui_error: string | null;

    /**
     * Local/optimistic blob URL OR server-provided audio_url for playback.
     * We keep this separate so playback works immediately.
     */
    ui_audio_url: string | null;

    /** Per-message upload progress 0-100, or null when no upload / upload done */
    ui_upload_progress: number | null;
    /** Local file thumbnails shown during upload (before server responds) */
    ui_local_file_previews: LocalFilePreview[] | null;

    /** Set to true when transcription times out or is known to have failed */
    ui_transcription_failed?: boolean;
};

const OPTIMISTIC_THRESHOLD = 1024 * 1024; // 1 MB

function totalPayloadSize(files?: File[], audio?: File): number {
    let size = 0;
    if (files) files.forEach((f) => (size += f.size));
    if (audio) size += audio.size;
    return size;
}

const nowIso = () => new Date().toISOString();

const makeUiId = () => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c: any = crypto as any;
        if (c?.randomUUID) return `c_${c.randomUUID()}`;
    } catch {}
    return `c_${Math.random().toString(36).slice(2)}_${Date.now()}`;
};

const toUiFromServer = (m: MessageResponse): UiMessage => {
    return {
        ...m,
        ui_id: String(m.id),
        server_id: m.id,
        ui_status: "sent" as const,
        ui_error: null,

        // ✅ if backend returns signed audio_url, use it for playback
        ui_audio_url: (m.audio_url ?? null) as any,
        ui_upload_progress: null,
        ui_local_file_previews: null,
    } as UiMessage;
};

const sortUiByCreatedAtAsc = (a: UiMessage, b: UiMessage) => {
    const aTime = new Date((a as any).updated_at ?? a.created_at).getTime();
    const bTime = new Date((b as any).updated_at ?? b.created_at).getTime();
    return aTime - bTime;
};

export const useConversation = (
    args?: { themeId?: UUID; conversationTitle?: string },
) => {
    const passedThemeId = args?.themeId ?? undefined;
    const conversationTitle = args?.conversationTitle;

    // ── Seed from AppBoot preloaded data (available immediately on boot) ──
    const {
        preloadedMessages: bootMessages,
        conversationId: bootConversationId,
    } = useAppBoot();

    const [themeId, setThemeId] = React.useState<UUID | null>(
        passedThemeId ??
            (localStorage.getItem("theme_id") as UUID | null),
    );

    const [conversation, setConversation] = React.useState<
        ConversationResponse | null
    >(null);

    const [messages, setMessages] = React.useState<UiMessage[]>([]);

    const [isLoading, setIsLoading] = React.useState(true);
    const [isBootstrapping, setIsBootstrapping] = React.useState(true);
    const [wsConnected, setWsConnected] = React.useState(false);

    // Pagination state (cursor-based: use oldest message's created_at)
    const [hasMore, setHasMore] = React.useState(true);
    const [isLoadingOlder, setIsLoadingOlder] = React.useState(false);
    const oldestCursorRef = React.useRef<string | null>(null);
    const hasMoreRef = React.useRef(true);
    const isLoadingOlderRef = React.useRef(false);

    // Forward pagination state (for "view in conversation" / bidirectional scroll)
    const [hasMoreNewer, setHasMoreNewer] = React.useState(false);
    const [isLoadingNewer, setIsLoadingNewer] = React.useState(false);
    const newestCursorRef = React.useRef<string | null>(null);
    const hasMoreNewerRef = React.useRef(false);
    const isLoadingNewerRef = React.useRef(false);
    const isViewingAroundRef = React.useRef(false);

    const wsRef = React.useRef<ReconnectingWs | null>(null);
    const serverIdToUiIdRef = React.useRef<Map<string, string>>(new Map());
    const loadMessagesRef = React.useRef<((id: UUID, opts?: { silent?: boolean }) => Promise<void>) | null>(null);
    const wsConversationIdRef = React.useRef<UUID | null>(null);
    const wsHasConnectedOnceRef = React.useRef(false);

    // ── Seed messages from AppBoot preload (runs once when preloaded data arrives) ──
    const didSeedFromBootRef = React.useRef(false);
    React.useEffect(() => {
        if (didSeedFromBootRef.current) return;
        if (bootMessages.length === 0) return;
        didSeedFromBootRef.current = true;

        const seeded = bootMessages.map(toUiFromServer);
        seeded.sort(sortUiByCreatedAtAsc);

        setMessages((prev) => {
            // Don't overwrite if bootstrap already loaded messages
            if (prev.length > 0) return prev;
            return seeded;
        });

        // Update pagination cursor from seeded data
        if (seeded.length > 0) {
            oldestCursorRef.current = seeded[0].created_at;
        }

        if (bootConversationId) {
            setConversation((prev) => prev ?? { id: bootConversationId } as any);
        }

        setIsLoading(false);
        setIsBootstrapping(false);
    }, [bootMessages, bootConversationId]);

    const findPendingMatchUiId = React.useCallback(
        (serverMsg: MessageResponse, prev: UiMessage[]) => {
            const myUserId = getUserId();
            const serverSenderId = String(serverMsg.sender_id ?? "");

            if (myUserId && serverSenderId !== String(myUserId)) return null;

            const content = String(serverMsg.content ?? "").trim();
            const hasFiles = !!serverMsg.has_files;
            const hasAudio = !!serverMsg.has_audio;

            for (let i = prev.length - 1; i >= 0; i--) {
                const m = prev[i];

                if (m.ui_status !== "pending") continue;
                if (m.server_id) continue;

                if (
                    myUserId && String(m.sender_id ?? "") !== String(myUserId)
                ) {
                    continue;
                }

                if (String(m.content ?? "").trim() !== content) continue;
                if (!!m.has_files !== hasFiles) continue;
                if (!!m.has_audio !== hasAudio) continue;

                const t0 = new Date(m.created_at).getTime();
                const t1 = new Date(serverMsg.created_at).getTime();
                const dt = Math.abs(t1 - t0);
                if (Number.isFinite(dt) && dt > 45_000) continue;

                return m.ui_id;
            }

            return null;
        },
        [],
    );

    const upsertServerMessage = React.useCallback(
        (serverMsg: MessageResponse) => {
            const serverId = String(serverMsg.id);

            setMessages((prev) => {
                const mappedUiId = serverIdToUiIdRef.current.get(serverId);

                if (mappedUiId) {
                    let changed = false;
                    const next = prev.map((m) => {
                        if (m.ui_id !== mappedUiId) return m;
                        changed = true;

                        // ✅ preserve optimistic ui_audio_url unless server provides audio_url
                        const nextUiAudio = (serverMsg.audio_url ?? null) ||
                            (m.ui_audio_url ?? null);

                        // Revoke old blob URL if server provided a real URL
                        if (serverMsg.audio_url && m.ui_audio_url?.startsWith("blob:")) {
                            try { URL.revokeObjectURL(m.ui_audio_url); } catch { /* ignore */ }
                        }

                        // Clean up local file preview blob URLs
                        m.ui_local_file_previews?.forEach((f) => {
                            if (f.previewUrl) try { URL.revokeObjectURL(f.previewUrl); } catch { /* ignore */ }
                        });

                        return {
                            ...m,
                            ...serverMsg,
                            ui_id: m.ui_id,
                            server_id: serverMsg.id,
                            ui_status: "sent" as const,
                            ui_error: null,
                            ui_audio_url: nextUiAudio,
                            ui_upload_progress: null,
                            ui_local_file_previews: null,
                        };
                    });

                    return changed ? next.sort(sortUiByCreatedAtAsc) : prev;
                }

                const pendingMatchUiId = findPendingMatchUiId(serverMsg, prev);
                if (pendingMatchUiId) {
                    serverIdToUiIdRef.current.set(serverId, pendingMatchUiId);

                    const next = prev.map((m) => {
                        if (m.ui_id !== pendingMatchUiId) return m;

                        const nextUiAudio = (serverMsg.audio_url ?? null) ||
                            (m.ui_audio_url ?? null);

                        // Revoke old blob URL if server provided a real URL
                        if (serverMsg.audio_url && m.ui_audio_url?.startsWith("blob:")) {
                            try { URL.revokeObjectURL(m.ui_audio_url); } catch { /* ignore */ }
                        }

                        // Clean up local file preview blob URLs
                        m.ui_local_file_previews?.forEach((f) => {
                            if (f.previewUrl) try { URL.revokeObjectURL(f.previewUrl); } catch { /* ignore */ }
                        });

                        return {
                            ...m,
                            ...serverMsg,
                            ui_id: m.ui_id,
                            server_id: serverMsg.id,
                            ui_status: "sent" as const,
                            ui_error: null,
                            ui_audio_url: nextUiAudio,
                            ui_upload_progress: null,
                            ui_local_file_previews: null,
                        };
                    });

                    return next.sort(sortUiByCreatedAtAsc);
                }

                const exists = prev.some(
                    (m) =>
                        String(m.server_id ?? "") === serverId ||
                        String(m.id) === serverId,
                );

                if (exists) {
                    const next = prev.map((m) => {
                        const isSame = String(m.server_id ?? m.id) === serverId;
                        if (!isSame) return m;

                        const nextUiAudio = (serverMsg.audio_url ?? null) ||
                            (m.ui_audio_url ?? null);

                        return {
                            ...m,
                            ...serverMsg,
                            ui_id: m.ui_id,
                            server_id: serverMsg.id,
                            ui_status: "sent" as const,
                            ui_error: null,
                            ui_audio_url: nextUiAudio,
                        };
                    });

                    serverIdToUiIdRef.current.set(
                        serverId,
                        prev.find((m) =>
                            String(m.server_id ?? m.id) === serverId
                        )?.ui_id ??
                            serverId,
                    );

                    return next.sort(sortUiByCreatedAtAsc);
                }

                // When viewing a historical slice, skip inserting new messages
                // (they'll be loaded via forward scroll)
                if (isViewingAroundRef.current && hasMoreNewerRef.current) {
                    return prev;
                }

                const ui = toUiFromServer(serverMsg);
                serverIdToUiIdRef.current.set(serverId, ui.ui_id);

                const next = [...prev, ui];
                next.sort(sortUiByCreatedAtAsc);
                return next;
            });
        },
        [findPendingMatchUiId],
    );

    const applySeenUpdate = React.useCallback(
        (messageIds: string[], seenAt: string, seenBy: string) => {
            if (!messageIds || messageIds.length === 0) return;

            const idSet = new Set(messageIds.map(String));

            setMessages((prev) => {
                let changed = false;
                const next = prev.map((m) => {
                    const sid = String(m.server_id ?? m.id ?? "");
                    if (!idSet.has(sid)) return m;

                    changed = true;
                    return {
                        ...m,
                        is_seen: true,
                        seen_at: seenAt,
                        seen_by: seenBy,
                    };
                });

                return changed ? next : prev;
            });
        },
        [],
    );

    const closeWs = React.useCallback(() => {
        const rws = wsRef.current;
        wsRef.current = null;
        if (rws) rws.dispose();
    }, []);

    const connectWs = React.useCallback(
        (conversationId: UUID) => {
            closeWs();
            wsConversationIdRef.current = conversationId;

            log("connect conversationId =", conversationId);

            const rws = createReconnectingWs({
                url: () => buildConversationWsUrl(conversationId),
                onOpen: () => {
                    log("ws open");
                    setWsConnected(true);

                    // Load messages on every WS open (first connect + reconnects)
                    if (wsConversationIdRef.current) {
                        const silent = wsHasConnectedOnceRef.current; // first connect shows loading, reconnects are silent
                        loadMessagesRef.current?.(wsConversationIdRef.current, { silent });
                    }
                    wsHasConnectedOnceRef.current = true;
                },
                onConnectionChange: (connected) => {
                    if (!connected) {
                        log("ws disconnected, will reconnect…");
                        setWsConnected(false);
                    }
                },
                onError: (err) => {
                    log("ws error:", err);
                },
                onMessage: (evt) => {
                    const parsed = safeJsonParse(evt.data);
                    if (!parsed) return;

                    const msg = parsed as WsIncoming;

                    if (msg.type === "message.created" && msg.data) {
                        upsertServerMessage(msg.data);
                        return;
                    }

                    // Handle background transcription updates
                    if (msg.type === "message.updated" && msg.data) {
                        // Validate minimal shape before upserting to prevent state corruption
                        const d = msg.data;
                        if (d.id && d.conversation_id && d.created_at) {
                            upsertServerMessage(d);
                        }
                        return;
                    }

                    if (msg.type === "message.seen" && msg.data) {
                        applySeenUpdate(
                            msg.data.message_ids || [],
                            msg.data.seen_at,
                            msg.data.seen_by,
                        );
                        return;
                    }
                },
            });

            wsRef.current = rws;
        },
        [closeWs, upsertServerMessage, applySeenUpdate],
    );

    const loadMessages = React.useCallback(
        async (conversationId: UUID, opts?: { silent?: boolean }) => {
            // Skip if we're viewing a historical slice (scroll-to-message).
            // loadMessagesAround set up cursors and hasMore/hasMoreNewer — a
            // full loadMessages would overwrite that state and jump to the tip.
            if (isViewingAroundRef.current) return;

            if (!opts?.silent) setIsLoading(true);
            try {
                const data = await listMessages(
                    conversationId,
                    PAGE_SIZE,
                );

                const serverUi = data.map(toUiFromServer);

                setMessages((prev) => {
                    const freshIds = new Set(
                        serverUi.map((m) => String(m.server_id ?? m.id)),
                    );

                    // Keep pending AND failed optimistic messages (they have no server_id)
                    const pending = prev.filter(
                        (m) => !m.server_id && (m.ui_status === "pending" || m.ui_status === "failed"),
                    );

                    // Preserve older messages that aren't in the fresh batch
                    // (the fresh batch contains the newest PAGE_SIZE messages)
                    const oldestFresh = serverUi.length > 0
                        ? [...serverUi].sort(sortUiByCreatedAtAsc)[0].created_at
                        : null;

                    const olderMessages = oldestFresh
                        ? prev.filter(
                              (m) =>
                                  m.server_id &&
                                  new Date(m.created_at).getTime() < new Date(oldestFresh).getTime() &&
                                  !freshIds.has(String(m.server_id ?? m.id)),
                          )
                        : [];

                    // Rebuild the server ID map
                    serverIdToUiIdRef.current.clear();
                    for (const m of [...olderMessages, ...serverUi]) {
                        serverIdToUiIdRef.current.set(
                            String(m.server_id ?? m.id),
                            m.ui_id,
                        );
                    }

                    const merged = [...olderMessages, ...serverUi, ...pending];
                    merged.sort(sortUiByCreatedAtAsc);

                    // Bail out if nothing changed — prevents unnecessary re-renders
                    if (
                        merged.length === prev.length &&
                        merged.every((m, i) =>
                            String(m.server_id ?? m.id) === String(prev[i].server_id ?? prev[i].id) &&
                            (m as any).updated_at === (prev[i] as any).updated_at
                        )
                    ) {
                        return prev;
                    }

                    return merged;
                });

                // Track cursor for pagination (oldest message's created_at)
                // Use the oldest message across ALL loaded messages (not just fresh batch)
                const allSorted = [...serverUi].sort(sortUiByCreatedAtAsc);
                if (allSorted.length > 0) {
                    // Preserve existing cursor if it's older than the fresh batch
                    const freshOldest = allSorted[0].created_at;
                    if (
                        !oldestCursorRef.current ||
                        new Date(freshOldest).getTime() < new Date(oldestCursorRef.current).getTime()
                    ) {
                        oldestCursorRef.current = freshOldest;
                    }
                } else {
                    oldestCursorRef.current = null;
                }
                hasMoreRef.current = oldestCursorRef.current !== null ? hasMoreRef.current : false;
                setHasMore(hasMoreRef.current);

                // Reset "around" mode — we're back at the tip
                isViewingAroundRef.current = false;
                hasMoreNewerRef.current = false;
                setHasMoreNewer(false);
                newestCursorRef.current = null;
            } catch (e) {
                log("listMessages failed:", e);
            } finally {
                if (!opts?.silent) setIsLoading(false);
            }
        },
        [],
    );

    // Keep ref updated so WS reconnect can call it
    loadMessagesRef.current = loadMessages;

    const ensureThemeId = React.useCallback(async (): Promise<UUID> => {
        if (passedThemeId) {
            localStorage.setItem("theme_id", passedThemeId);
            setThemeId(passedThemeId);
            return passedThemeId;
        }

        if (themeId) return themeId;

        const myUserId = getUserId();
        if (!myUserId) {
            throw new Error(
                "[use-conversation] Missing user_id. Set localStorage.user_id after login so we can create a theme.",
            );
        }

        const themes = await listMyThemes();
        const existing = pickDefaultTheme(themes);

        if (existing?.id) {
            localStorage.setItem("theme_id", existing.id);
            setThemeId(existing.id);
            return existing.id;
        }

        const created = await createTheme({
            profile_id: myUserId,
            title: "Main",
            is_onboarding: false,
            priority: null,
        });

        localStorage.setItem("theme_id", created.id);
        setThemeId(created.id);
        return created.id;
    }, [passedThemeId, themeId]);

    // Bootstrap runs exactly once on mount — no callback deps that could restart it.
    // Uses refs + localStorage to read current values without triggering re-runs.
    const bootstrapRanRef = React.useRef(false);
    const safetyTimerRef = React.useRef<number>(0);

    React.useEffect(() => {
        if (bootstrapRanRef.current) return;
        bootstrapRanRef.current = true;

        let alive = true;

        const run = async () => {
            log("bootstrap: starting");
            setIsBootstrapping(true);
            setIsLoading(true);

            try {
                // ── Step 0: ensure auth token is valid before any API calls ──
                // The bootstrap runs on mount (before auth boot may finish).
                // Raw fetch() calls have no 401 retry — must have a valid token first.
                const tokenValid = await ensureValidToken();
                log("bootstrap: ensureValidToken →", tokenValid);
                if (!tokenValid || !alive) {
                    log("bootstrap: no valid token, deferring");
                    setMessages([]);
                    setIsLoading(false);
                    setIsBootstrapping(false);
                    return;
                }

                // ── Step 1: resolve conversation_id ──
                // Prefer cached or AppBoot-discovered conversationId
                let convId = localStorage.getItem("conversation_id") as UUID | null;
                log("bootstrap: cached conversation_id =", convId);

                if (!convId) {
                    // Resolve canonical conversation from backend
                    // (same logic the admin panel uses — guarantees both sides see the same conversation)
                    try {
                        const canonical = await getCanonicalConversation();
                        if (!alive) return;
                        if (canonical?.id) {
                            convId = canonical.id as UUID;
                            const tid = canonical.theme_id as UUID;
                            localStorage.setItem("theme_id", tid);
                            localStorage.setItem("conversation_id", convId);
                            setThemeId(tid);
                            setConversation(canonical);
                            log("bootstrap: canonical conversation →", convId, "theme →", tid);
                        }
                    } catch (e) {
                        log("bootstrap: canonical endpoint failed, falling back:", e);
                        if (!alive) return;
                        // Fallback: old theme→conversation discovery
                        let tid = await ensureThemeId();
                        log("bootstrap: ensureThemeId →", tid);
                        if (!alive) return;

                        let convs = await listConversationsByTheme(tid);
                        log("bootstrap: listConversationsByTheme →", convs?.length, "results");
                        if (!alive) return;

                        if (!convs?.length) {
                            log("bootstrap: stale theme_id, re-discovering…");
                            const themes = await listMyThemes();
                            if (!alive) return;
                            const nonOnboarding = themes.filter((t) => t.is_onboarding === false);
                            const orderedThemes = [...nonOnboarding, ...themes.filter((t) => t.is_onboarding !== false)];

                            for (const theme of orderedThemes) {
                                if (!theme?.id || theme.id === tid) continue;
                                const c = await listConversationsByTheme(theme.id);
                                if (!alive) return;
                                if (c?.length) {
                                    convs = c;
                                    tid = theme.id as UUID;
                                    localStorage.setItem("theme_id", tid);
                                    setThemeId(tid);
                                    log("bootstrap: found conversations under theme", tid);
                                    break;
                                }
                            }
                        }

                        const existing = convs?.[0] ?? null;
                        setConversation(existing);

                        if (existing?.id) {
                            convId = existing.id as UUID;
                            localStorage.setItem("conversation_id", convId);
                        }
                    }
                }

                if (!convId) {
                    log("bootstrap: no conversation found, done");
                    // No conversation exists yet
                    setMessages([]);
                    setIsLoading(false);
                    setIsBootstrapping(false);
                    return;
                }

                // ── Step 2: connect WebSocket (onOpen will load messages) ──
                log("bootstrap: connecting WS for", convId);
                connectWs(convId);
                setIsBootstrapping(false);

                // Safety net: if WS fails to connect (stale token, network), isLoading
                // would stay true forever since it relies on onOpen → loadMessages → finally.
                // Clear it after a generous timeout so the user isn't stuck on a spinner.
                safetyTimerRef.current = window.setTimeout(() => {
                    if (alive) {
                        log("bootstrap: safety timeout — clearing loading");
                        setIsLoading(false);
                    }
                }, 15_000);

                // ── Step 3: set conversation state if not set from discovery ──
                setConversation((prev) => {
                    if (prev) return prev;
                    // Minimal shape so conversationId is exposed from hook
                    return { id: convId } as any;
                });
            } catch (e) {
                log("bootstrap failed:", e);
                setConversation(null);
                setMessages([]);
                if (alive) {
                    setIsLoading(false);
                    setIsBootstrapping(false);
                }
            }
        };

        run();

        return () => {
            alive = false;
            closeWs();
            clearTimeout(safetyTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ensureConversation = React.useCallback(
        async (): Promise<ConversationResponse> => {
            if (conversation?.id) return conversation;

            const tid = await ensureThemeId();

            const created = await createConversation({
                theme_id: tid,
                title: conversationTitle ?? null,
            });

            setConversation(created);

            connectWs(created.id);
            await loadMessages(created.id);

            return created;
        },
        [
            conversation,
            conversationTitle,
            ensureThemeId,
            connectWs,
            loadMessages,
        ],
    );

    const loadOlderMessages = React.useCallback(async () => {
        const convId = conversation?.id;
        if (!convId || isLoadingOlderRef.current || !hasMoreRef.current) return;

        const cursor = oldestCursorRef.current;
        if (!cursor) return;

        isLoadingOlderRef.current = true;
        setIsLoadingOlder(true);
        try {
            const data = await listMessages(convId, PAGE_SIZE, cursor);

            const olderUi = data.map(toUiFromServer);

            if (data.length < PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
            }

            // Update cursor to the oldest message in the new batch
            if (olderUi.length > 0) {
                const sorted = [...olderUi].sort(sortUiByCreatedAtAsc);
                oldestCursorRef.current = sorted[0].created_at;
            }

            setMessages((prev) => {
                // Register in ID map
                for (const m of olderUi) {
                    serverIdToUiIdRef.current.set(
                        String(m.server_id ?? m.id),
                        m.ui_id,
                    );
                }

                // Filter out duplicates
                const existingIds = new Set(
                    prev.map((m) => String(m.server_id ?? m.id)),
                );
                const newOlder = olderUi.filter(
                    (m) => !existingIds.has(String(m.server_id ?? m.id)),
                );

                const merged = [...newOlder, ...prev];
                merged.sort(sortUiByCreatedAtAsc);
                return merged;
            });
        } catch (e) {
            log("loadOlderMessages failed:", e);
        } finally {
            isLoadingOlderRef.current = false;
            setIsLoadingOlder(false);
        }
    }, [conversation?.id]);

    const loadMessagesAround = React.useCallback(
        async (conversationId: UUID, messageId: UUID): Promise<string | null> => {
            // Block loadMessages immediately (before await) so the WS onOpen
            // callback doesn't race and overwrite our messages.
            isViewingAroundRef.current = true;
            setIsLoading(true);
            try {
                const response = await listMessagesAround(conversationId, messageId);
                const uiMessages = response.messages.map(toUiFromServer);
                uiMessages.sort(sortUiByCreatedAtAsc);

                serverIdToUiIdRef.current.clear();
                for (const m of uiMessages) {
                    serverIdToUiIdRef.current.set(String(m.server_id ?? m.id), m.ui_id);
                }

                setMessages(uiMessages);

                if (uiMessages.length > 0) {
                    oldestCursorRef.current = uiMessages[0].created_at;
                    newestCursorRef.current = uiMessages[uiMessages.length - 1].created_at;
                }

                hasMoreRef.current = response.has_older;
                setHasMore(response.has_older);
                hasMoreNewerRef.current = response.has_newer;
                setHasMoreNewer(response.has_newer);
                isViewingAroundRef.current = response.has_newer;

                return response.target_message_id;
            } catch (e) {
                log("loadMessagesAround failed:", e);
                return null;
            } finally {
                setIsLoading(false);
            }
        },
        [],
    );

    const loadNewerMessages = React.useCallback(async () => {
        const convId = conversation?.id;
        if (!convId || isLoadingNewerRef.current || !hasMoreNewerRef.current) return;

        const cursor = newestCursorRef.current;
        if (!cursor) return;

        isLoadingNewerRef.current = true;
        setIsLoadingNewer(true);
        try {
            const data = await listMessages(convId, PAGE_SIZE, undefined, cursor);
            const newerUi = data.map(toUiFromServer);

            if (data.length < PAGE_SIZE) {
                hasMoreNewerRef.current = false;
                setHasMoreNewer(false);
                isViewingAroundRef.current = false; // reached the tip
            }

            if (newerUi.length > 0) {
                const sorted = [...newerUi].sort(sortUiByCreatedAtAsc);
                newestCursorRef.current = sorted[sorted.length - 1].created_at;
            }

            setMessages((prev) => {
                for (const m of newerUi) {
                    serverIdToUiIdRef.current.set(String(m.server_id ?? m.id), m.ui_id);
                }
                const existingIds = new Set(prev.map((m) => String(m.server_id ?? m.id)));
                const newNewer = newerUi.filter(
                    (m) => !existingIds.has(String(m.server_id ?? m.id)),
                );
                const merged = [...prev, ...newNewer];
                merged.sort(sortUiByCreatedAtAsc);
                return merged;
            });
        } catch (e) {
            log("loadNewerMessages failed:", e);
        } finally {
            isLoadingNewerRef.current = false;
            setIsLoadingNewer(false);
        }
    }, [conversation?.id]);

    const submittingRef = React.useRef(false);
    const abortControllersRef = React.useRef<Map<string, AbortController>>(new Map());
    const retryPayloadRef = React.useRef<Map<string, {
        text: string; files?: File[]; audio?: File;
        sentViaSms?: boolean; opts?: {
            transcribePending?: boolean;
            audioDuration?: number;
        };
    }>>(new Map());

    /**
     * Core send logic shared by sendMessage and retryMessage.
     * Returns the server-saved message on success, throws on failure.
     */
    const executeSend = React.useCallback(
        async (
            uiId: string,
            conversationId: string,
            trimmed: string,
            files: File[] | undefined,
            audio: File | undefined,
            audioUrl: string | null,
            _sentViaSms: boolean,
            opts?: {
                transcribePending?: boolean;
                audioDuration?: number;
            },
        ) => {
            const payloadSize = totalPayloadSize(files, audio);
            const showProgress = payloadSize >= OPTIMISTIC_THRESHOLD;

            // Per-message progress updater
            const updateProgress = showProgress
                ? (percent: number) => {
                      setMessages((prev) =>
                          prev.map((m) =>
                              m.ui_id === uiId
                                  ? { ...m, ui_upload_progress: percent }
                                  : m,
                          ),
                      );
                  }
                : undefined;

            // Abort controller for large uploads
            let controller: AbortController | undefined;
            if (showProgress) {
                controller = new AbortController();
                abortControllersRef.current.set(uiId, controller);
            }

            try {
                let saved: MessageResponse;

                if ((files && files.length > 0) || audio) {
                    saved = await createMessageMultipart({
                        conversation_id: conversationId,
                        content: trimmed || "",
                        ...(files && files.length > 0 && { files }),
                        ...(audio && { audio }),
                        ...(updateProgress && { onUploadProgress: updateProgress }),
                        ...(opts?.transcribePending && { transcribe_pending: true }),
                        ...(opts?.audioDuration != null && { audio_duration: opts.audioDuration }),
                        ...(controller && { signal: controller.signal }),
                    });
                } else {
                    saved = await createMessageJson({
                        conversation_id: conversationId,
                        content: trimmed,
                    });
                }

                serverIdToUiIdRef.current.set(String(saved.id), uiId);
                retryPayloadRef.current.delete(uiId);

                trackEvent('message_sent', {
                    type: audio ? 'audio' : (files && files.length > 0) ? 'file' : 'text',
                    has_audio: !!audio,
                    file_count: files?.length ?? 0,
                });

                setMessages((prev) => {
                    const next = prev.map((m) => {
                        if (m.ui_id !== uiId) return m;

                        // Revoke local file preview blob URLs
                        m.ui_local_file_previews?.forEach((f) => {
                            if (f.previewUrl) try { URL.revokeObjectURL(f.previewUrl); } catch {}
                        });

                        // Keep blob url unless server gave us audio_url
                        const nextUiAudio = (saved.audio_url ?? null) ||
                            (m.ui_audio_url ?? null);

                        return {
                            ...m,
                            ...saved,
                            ui_id: m.ui_id,
                            server_id: saved.id,
                            ui_status: "sent" as const,
                            ui_error: null,
                            ui_audio_url: nextUiAudio,
                            ui_upload_progress: null,
                            ui_local_file_previews: null,
                        };
                    });
                    next.sort(sortUiByCreatedAtAsc);
                    return next;
                });
            } catch (e: any) {
                // Don't mark as failed if it was a deliberate cancel
                if (e?.name === "AbortError") throw e;

                trackEvent('message_send_failed', { error: e?.message });
                log("sendMessage failed:", e);

                // Revoke the audio blob URL to prevent memory leaks
                if (audioUrl) {
                    try { URL.revokeObjectURL(audioUrl); } catch {}
                }

                setMessages((prev) => {
                    const next = prev.map((m) => {
                        if (m.ui_id !== uiId) return m;
                        return {
                            ...m,
                            ui_status: "failed" as const,
                            ui_error: getUserFriendlyErrorMessage(e),
                            ui_audio_url: null,
                            ui_upload_progress: null,
                        };
                    });
                    return next;
                });

                throw e;
            } finally {
                abortControllersRef.current.delete(uiId);
            }
        },
        [ensureConversation],
    );

    const sendMessage = React.useCallback(
        async (
            text: string,
            files?: File[],
            audio?: File,
            sentViaSms?: boolean,
            opts?: {
                transcribePending?: boolean;
                onUploadProgress?: (percent: number) => void;
                audioDuration?: number;
            },
        ) => {
            const trimmed = (text || "").trim();
            if (!trimmed && (!files || files.length === 0) && !audio) return;

            // Layer 1: prevent concurrent sends at the hook level
            if (submittingRef.current) return;
            submittingRef.current = true;

            try {

            const tokenValid = await ensureValidToken();
            if (!tokenValid) {
                throw new Error("Your session expired. Please sign in again.");
            }

            const conv = await ensureConversation();
            const conversationId = conv.id;

            const uiId = makeUiId();
            const myUserId = getUserId() || "unknown";

            const audioUrl = audio ? URL.createObjectURL(audio) : null;

            const payloadSize = totalPayloadSize(files, audio);
            const showProgress = payloadSize >= OPTIMISTIC_THRESHOLD;

            // Generate local file previews for optimistic rendering
            const localPreviews: LocalFilePreview[] = (files || []).map((f) => ({
                name: f.name,
                type: f.type,
                size: f.size,
                previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
            }));

            const optimistic: UiMessage = {
                ...({
                    ...({
                        conversation_id: conversationId,
                        sender_id: myUserId as any,
                        content: trimmed || "",
                        created_at: nowIso(),
                        updated_at: null,
                        has_files: !!(files && files.length > 0),
                        has_audio: !!audio,
                        is_seen: false,
                        seen_at: null,
                        seen_by: null,
                        files: [],
                        audio_id: null,
                        sent_via_sms: sentViaSms || false,
                        audio_url: null,
                    } satisfies Omit<MessageResponse, "id">),
                } as any),
                id: uiId as any,
                ui_id: uiId,
                server_id: null,
                ui_status: "pending" as const,
                ui_error: null,
                ui_audio_url: audioUrl,
                ui_upload_progress: showProgress ? 0 : null,
                ui_local_file_previews: localPreviews.length > 0 ? localPreviews : null,
            };

            setMessages((prev) => {
                const next = [...prev, optimistic];
                next.sort(sortUiByCreatedAtAsc);
                return next;
            });

            // Store payload for retry
            retryPayloadRef.current.set(uiId, {
                text: trimmed,
                ...(files && { files }),
                ...(audio && { audio }),
                ...(sentViaSms != null && { sentViaSms }),
                ...(opts && {
                    opts: {
                        ...(opts.transcribePending != null && { transcribePending: opts.transcribePending }),
                        ...(opts.audioDuration != null && { audioDuration: opts.audioDuration }),
                    },
                }),
            });

            try {
                await executeSend(uiId, conversationId, trimmed, files, audio, audioUrl, sentViaSms || false, opts);
            } catch (e: any) {
                // Silently swallow cancellation — user already handled via cancelUpload
                if (e?.name === "AbortError") return;
                throw e;
            }

            } finally {
                submittingRef.current = false;
            }
        },
        [ensureConversation, executeSend],
    );

    const retryMessage = React.useCallback(
        async (uiId: string) => {
            const payload = retryPayloadRef.current.get(uiId);
            if (!payload) return;

            if (submittingRef.current) return;
            submittingRef.current = true;

            try {
                const tokenValid = await ensureValidToken();
                if (!tokenValid) {
                    throw new Error("Your session expired. Please sign in again.");
                }

                const conv = await ensureConversation();
                const conversationId = conv.id;

                const audioUrl = payload.audio ? URL.createObjectURL(payload.audio) : null;

                const payloadSize = totalPayloadSize(payload.files, payload.audio);
                const showProgress = payloadSize >= OPTIMISTIC_THRESHOLD;

                // Regenerate local file previews
                const localPreviews: LocalFilePreview[] = (payload.files || []).map((f) => ({
                    name: f.name,
                    type: f.type,
                    size: f.size,
                    previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
                }));

                // Reset message to pending
                setMessages((prev) =>
                    prev.map((m) => {
                        if (m.ui_id !== uiId) return m;
                        // Revoke old preview URLs
                        m.ui_local_file_previews?.forEach((f) => {
                            if (f.previewUrl) try { URL.revokeObjectURL(f.previewUrl); } catch {}
                        });
                        return {
                            ...m,
                            ui_status: "pending" as const,
                            ui_error: null,
                            ui_upload_progress: showProgress ? 0 : null,
                            ui_audio_url: audioUrl,
                            ui_local_file_previews: localPreviews.length > 0 ? localPreviews : null,
                        };
                    }),
                );

                await executeSend(
                    uiId, conversationId, payload.text,
                    payload.files, payload.audio, audioUrl,
                    payload.sentViaSms || false, payload.opts,
                );
            } catch (e: any) {
                // Silently swallow cancellation
                if (e?.name === "AbortError") return;
                // Other errors already marked on message by executeSend
                log("retryMessage failed:", e);
            } finally {
                submittingRef.current = false;
            }
        },
        [ensureConversation, executeSend],
    );

    const cancelUpload = React.useCallback((uiId: string) => {
        // Abort the XHR
        abortControllersRef.current.get(uiId)?.abort();
        abortControllersRef.current.delete(uiId);
        retryPayloadRef.current.delete(uiId);

        // Revoke blob URLs and remove the message
        setMessages((prev) => {
            const msg = prev.find((m) => m.ui_id === uiId);
            if (msg) {
                if (msg.ui_audio_url?.startsWith("blob:")) {
                    try { URL.revokeObjectURL(msg.ui_audio_url); } catch {}
                }
                msg.ui_local_file_previews?.forEach((f) => {
                    if (f.previewUrl) try { URL.revokeObjectURL(f.previewUrl); } catch {}
                });
            }
            return prev.filter((m) => m.ui_id !== uiId);
        });
    }, []);

    const notifyConversationOpened = React.useCallback(() => {
        const convId = conversation?.id;
        const rws = wsRef.current;
        if (!convId || !rws) return;

        rws.send({
            type: "conversation.opened",
            conversation_id: convId,
        });
    }, [conversation?.id]);

    /** Mark a file as deleted in local message state (sets deleted_at). */
    const markFileDeleted = React.useCallback((fileId: string) => {
        setMessages((prev) =>
            prev.map((msg) => {
                if (!msg.files?.length) return msg;
                const idx = msg.files.findIndex((f) => f.id === fileId);
                if (idx === -1) return msg;
                const updatedFiles = msg.files.map((f) =>
                    f.id === fileId ? { ...f, deleted_at: new Date().toISOString() } : f,
                );
                return { ...msg, files: updatedFiles };
            }),
        );
    }, []);

    const reloadMessages = React.useCallback(() => {
        const convId = conversation?.id;
        if (convId) loadMessages(convId as UUID, { silent: true });
    }, [conversation?.id, loadMessages]);

    const clearMessages = React.useCallback(() => {
        setMessages([]);
        setIsLoading(true);
    }, []);

    return {
        themeId,
        conversation,
        conversationId: conversation?.id ?? null,
        messages,
        isLoading,
        isBootstrapping,
        wsConnected,
        sendMessage,
        retryMessage,
        cancelUpload,
        notifyConversationOpened,
        normalizeAvatarUrl,
        loadOlderMessages,
        hasMore,
        isLoadingOlder,
        loadMessagesAround,
        loadNewerMessages,
        hasMoreNewer,
        isLoadingNewer,
        clearMessages,
        markFileDeleted,
        reloadMessages,
    };
};
